/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

// antd:
import { Form } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
// import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getAppState, changeAppState, getTheme, getLng, getDict, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

type Tlink = {
  classicLink:boolean,
  to?:string,
  rel?:string,
  replace?:boolean,
  preventScrollReset?:boolean,
  reloadDocument?:boolean,
  relative?:"path"
};

const RZlink:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZlink.displayName = "Link";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {color, backgroundColor, borders} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const linkType = useMemo<Tlink>(() => {
    const typ:Tlink = {classicLink: false};
    (type || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["classicLink", "classiclink", "classic", "a"].indexOf(tt)) { typ.classicLink = true }
      else if (tt === "blank") { typ.rel = "noopener" }
      else if (tt === "replace") { typ.replace = true }
      else if (tt === "preventScrollReset") { typ.preventScrollReset = true }
      else if (tt === "reloadDocument") { typ.reloadDocument = true }
      else if (tt === "relative") { typ.relative = "path" }

      if (elState?.attributes?.target === "_blank") { typ.rel = "noopener" }
      if (elState?.attributes?.classicLink === "true" || attributes?.classicLink === "true") { typ.classicLink = true }
      if (elState?.attributes?.to || elState?.attributes?.href) { typ.to = (elState?.attributes?.to || elState?.attributes?.href) }
      if (attributes?.onClick || attributes?.onclick) { typ.to = "#" }
    });
    return typ;
  }, [type, elState?.attributes, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!~userClss.indexOf("rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, borders, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  const state = useMemo<{[key:string]:any}>(() => {
    let jsn;
    if (elState?.attributes?.state) {
      if (rz.isString(elState.attributes.state) && elState.attributes.state.startsWith("{") && elState.attributes.state.endsWith("}")) {
        const jsnb = (rz.strReplaceAll(elState.attributes.state, "'", "\"")).trim(); try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
      }
      else if (rz.isObject(elState.attributes.state)) {
        jsn = {...elState.attributes.state}
      }
    }
    else if (attributes?.state) {
      if (rz.isString(attributes.state) && attributes.state.startsWith("{") && attributes.state.endsWith("}")) {
        const jsnb = (rz.strReplaceAll(attributes.state, "'", "\"")).trim(); try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
      }
      else if (rz.isObject(attributes.state)) {
        jsn = {...attributes.state}
      }
    }
    return jsn;
  }, [attributes.state, elState.attributes.state]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState?.value || elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.value, elState.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const onClick = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    if (fnc.config?.isDev) console.log({action: "<Link key='" + key + "'> -> onClick()"});
    if (rootNamespace && (attributes.onClick || attributes.onclick) && rootNamespace[attributes.onClick || attributes.onclick]) {
      const clientFNC = rootNamespace[attributes.onClick || attributes.onclick];
      const handleClientResult = (ret:any) => {
        if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}) }
      };
      if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe});
        // Обработчик onClick может быть async, поэтому проверяем Promise у результата вызова, а не у самой функции.
        if (rz.isPromise(ret)) { ret.then(handleClientResult).catch((err) => { console.log(err) }) }
        else { handleClientResult(ret) }
      }
    }
  }, [key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, navigate, safe]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // eslint-disable-next-line max-len
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  if (linkType.classicLink) {
    return wrapperForm(
      // eslint-disable-next-line react/no-unknown-property
      <a key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
        target={linkType.rel === "noopener" ? "_blank" : "_self"}
        rel={linkType.rel === "noopener" ? "noreferrer" : undefined}
        href={linkType.to || "#"}
        onClick={onClick}
      >
        {/* @ts-ignore */ }
        {children && children.length ? children : title}
      </a>
    );
  }

  return wrapperForm(
    <Link key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
      to={linkType.to || "#"}
      rel={linkType.rel}
      target={linkType.rel === "noopener" ? "_blank" : "_self"}
      replace={linkType.replace}
      reloadDocument={linkType.reloadDocument}
      preventScrollReset={linkType.preventScrollReset}
      relative={linkType.relative}
      state={state}
      onClick={onClick}
    >
      {/* @ts-ignore */ }
      {children && children.length ? children : title}
    </Link>
  );
}

export default RZlink;
/* eslint-enable @typescript-eslint/no-unused-vars */
